/**
 * The one door every Next Steps annotation passes through on its way to and from
 * the API: notes, highlights (and the notes nested inside them), personal tags and
 * blank answers.
 *
 * Why one door. End-to-end encryption (docs/plans/2026-09-02-next-steps-e2e-encryption.md,
 * section 6.7) seals an annotation on the way out and opens it on the way in, and
 * the courses store used to build every one of those HTTP calls inline, in more
 * than twenty places. This module is where the sealing and opening will attach,
 * so the store keeps speaking plaintext and no component, mixin or page object
 * ever learns that the wire carries envelopes.
 *
 * Two hooks, both identity functions until the read and write tickets replace
 * them. In this file, "seal" is the request side and "open" the response side:
 *
 *   request(context, body)  — the body of a create or update, just before it is
 *                             sent. Returns what goes on the wire (sealing).
 *   response(context, row)  — one row as the API returned it, whether from a
 *                             read or as the echo of a write. Returns what the
 *                             store gets to see (opening).
 *
 * Either may return a value or a promise (SubtleCrypto is asynchronous). Both
 * receive the same `context`, which is exactly what an envelope's additional
 * authenticated data is built from (spec section 6.3):
 *
 *   { memberId, type, itemId }
 *
 *   type    one of ANNOTATION_TYPE: 'note' | 'highlight' | 'tag' | 'blank'
 *   itemId  the row's id for a note, highlight or tag — null on a create until
 *           client-generated ids arrive — and `programSlug/lessonSlug/blockId`
 *           for a blank answer, the same composite the upsert is keyed on.
 *
 * Note-to-tag links and deletes carry no annotation content (spec section 6.4),
 * so they go through this module for the sake of having one place, but neither
 * hook is invoked for them.
 *
 * Two things the sealing hook will need that this seam does not yet provide, so
 * the write ticket (#1543) knows where it stands. The request hook sees exactly
 * the body the store sends, and an update often sends part of a row (a colour-only
 * highlight PATCH, a tag rename, the heal write-back's four anchor fields); an
 * envelope holds the whole field set and the API refuses plaintext beside a
 * payload, so an update must either send the whole row (the store holds it) or
 * the context must grow a `current` row. And `memberId` is null until the profile
 * has loaded, which a deep link into a lesson can beat; a hook must refuse to seal
 * under a null member rather than bind a row to "null:note:<id>" for ever.
 *
 * As built (#1543): the sealing hook refuses a null or mismatched member, and
 * it took the first of those two options — the STORE sends the whole content
 * set on an update, and the hook REFUSES one that does not, rather than sealing
 * a subset. It has to: a `payload` replaces the row's payload column and the
 * server nulls the plaintext, so a partial patch is not a partial write but a
 * destroyed row. Three shapes were doing exactly that before the guard landed —
 * the heal write-back's four anchors, a colour-only highlight PATCH (no
 * `pageIndex`, and no quote when the reader had none), and a tag rename, which
 * both callers dispatch with a name alone. `courses.js` now fills the unchanged
 * half from the stored row at each of those, and `sealed-writes.js`'s
 * REQUIRED_ON_UPDATE is the backstop for the next caller. The context did NOT
 * need to grow a `current` row.
 * The emptied blank's delete is `deleteBlankAnswer` below, reached through the
 * DELETE_BLANK_ANSWER sentinel. The notes manifest is added by the ticket that
 * first sends it.
 *
 * What this module deliberately does NOT do: it does not catch. An axios error
 * propagates exactly as it did when the store called axios itself, so every
 * existing rollback and read-cache fallback keeps working. It also uses the
 * shared axios default instance, not a private one, so the app's interceptors
 * (auth header guard, 401 handling, the mock error and timeout interceptors)
 * still apply, and `jest.mock('axios')` in the store specs still sees every call.
 */
import axios from 'axios';
import { ANNOTATION_REQUEST, countAnnotationRequest } from '@/utils/courses-autosave/telemetry';

export const ANNOTATION_TYPE = Object.freeze({
  NOTE: 'note',
  HIGHLIGHT: 'highlight',
  TAG: 'tag',
  BLANK: 'blank',
});

/**
 * What a request hook returns instead of a body when the write it was handed is
 * really a deletion: an emptied blank answer deletes its row rather than
 * storing an envelope of an empty string (spec section 6.4, decision 30). Only
 * the hook can tell — a member without a bundle still PUTs the empty string,
 * which is what the `next-steps-answers` write-fallback expects — so the
 * decision is the sealing side's and this is how it says so.
 *
 * A sentinel rather than a `{ delete: true }` body, so no field name can ever
 * collide with a real one, and frozen so a hook cannot mutate the marker every
 * other caller compares against.
 */
export const DELETE_BLANK_ANSWER = Object.freeze({ annotationTransport: 'delete-blank-answer' });

/**
 * Whether a sealed value is "delete this blank", however it reached us.
 *
 * ⚠️ COMPARING AGAINST THE SENTINEL BY IDENTITY IS NOT ENOUGH, and had not been
 * for longer than it looked. The sentinel goes into the outbox like any other
 * sealed body, and what comes back out of Preferences is a PARSED OBJECT — a
 * different one, every time. So a queued blank-clear has always been
 * `{ annotationTransport: 'delete-blank-answer' }` on disk and never `null`,
 * which is what the drain's `sealed === null` branches were written for.
 *
 * It carries a value now (its `expectedRevision`), which makes the same test
 * necessary rather than merely correct: an identity check would send a delete
 * that named no revision and silently clear a row another device had answered.
 *
 * `null` is NOT a delete to this predicate — `sendSealed` and the drain test for
 * it separately, as "an emptied blank with no sentinel" — and never was, though
 * an earlier version of this comment said otherwise.
 *
 * @param {unknown} value
 * @returns {boolean}
 */
export const isDeleteBlankAnswer = value =>
  value === DELETE_BLANK_ANSWER ||
  (value !== null &&
    typeof value === 'object' &&
    value.annotationTransport === DELETE_BLANK_ANSWER.annotationTransport);

const identity = (context, value) => value;
const IDENTITY_HOOKS = Object.freeze({ request: identity, response: identity });

let hooks = IDENTITY_HOOKS;

/**
 * Attach a request hook, a response hook, or both. A side that is not given keeps
 * whatever it already had, so the read path and the write path can attach
 * independently.
 */
export function setAnnotationTransportHooks({ request, response } = {}) {
  hooks = Object.freeze({
    request: typeof request === 'function' ? request : hooks.request,
    response: typeof response === 'function' ? response : hooks.response,
  });
}

/** Back to identity on both sides. Specs call this between cases. */
export function resetAnnotationTransportHooks() {
  hooks = IDENTITY_HOOKS;
}

/**
 * The item id an envelope for a blank answer is authenticated against.
 *
 * The same composite as `courses-crypto/envelope.js#blankItemId`, and
 * DELIBERATELY not that function: the crypto one asserts every part is a
 * non-empty string, because a hole there means an envelope bound to the wrong
 * row. This one is also asked to name a row the API answered with — a PUT that
 * returns `{}` reaches `openBlank` with no `blockId` — and a throw there turns a
 * write that landed into a failed send the engine retries. Tried as one function
 * on 2026-09-16; two suites said why not.
 */
export function blankAnswerItemId({ programSlug, lessonSlug, blockId }) {
  return `${programSlug}/${lessonSlug}/${blockId}`;
}

const apiUrl = path => `${process.env.VUE_APP_API_URL}${path}`;

// The config shape the store has always sent: `headers` alone, or `headers` plus
// `params` where the endpoint takes a query. A read keeps it byte-for-byte (the
// request-contract specs assert on it); a write adds the deadline below.
const withAuth = ({ token }, extra) => ({
  headers: { Authorization: `Bearer ${token}` },
  ...extra,
});

/**
 * How long a WRITE may sit on the wire before it is failed. This transport set
 * no axios timeout, and neither WebView bounds an XHR on its own the way a
 * native client would (iOS's 60 s applies to the URL loading system, not to a
 * connection the server keeps half-open; Android's has none), so a request the
 * network swallows could hang for as long as the OS took to notice — minutes.
 * The autosave engine sends one request per row at a time, so a hung write
 * wedged its row: new keystrokes parked behind it, `retryNow` skipped it, and
 * nothing said so.
 *
 * axios fails a timed-out request with `code: 'ECONNABORTED'` and no
 * `response`, which the engine's classifier already reads as "no network":
 * retried, and not counted against the row. The value sits above the
 * client-observed p99 of a production note save (~32 s on 2026-09-12), so a
 * slow save that would have landed still lands; only the hung ones are cut.
 *
 * Writes only. A read that hangs is a spinner, and the read paths have their
 * own fallbacks; changing their behaviour is not this release's business.
 */
export const ANNOTATION_WRITE_TIMEOUT_MS = 45000;

const withWriteAuth = (auth, extra) =>
  withAuth(auth, { timeout: ANNOTATION_WRITE_TIMEOUT_MS, ...extra });

const hookContext = (auth, type, itemId) => ({
  memberId: auth?.memberId ?? null,
  type,
  itemId: itemId ?? null,
});

// `options` is a third argument rather than a field on the context, so the
// context keeps the shape the request-contract specs pin and a two-argument
// hook never sees it. Only `sealAnnotation` passes one; a per-type write passes
// `undefined`.
const sealBody = (auth, type, itemId, body, options) =>
  Promise.resolve(hooks.request(hookContext(auth, type, itemId), body, options));

const openRow = (auth, type, itemId, row) =>
  Promise.resolve(hooks.response(hookContext(auth, type, itemId), row));

// A row that is not there (a write the API answered with no body) passes through
// untouched: there is nothing to open, and the store already copes with it.
const openNote = (auth, row) => (row ? openRow(auth, ANNOTATION_TYPE.NOTE, row.id, row) : row);

const openTag = (auth, row) => (row ? openRow(auth, ANNOTATION_TYPE.TAG, row.id, row) : row);

// A highlight carries its notes; each is an annotation in its own right, sealed
// under its own id, so each is opened as a note after the highlight itself.
async function openHighlight(auth, row) {
  if (!row) return row;
  const highlight = await openRow(auth, ANNOTATION_TYPE.HIGHLIGHT, row.id, row);
  if (!Array.isArray(highlight?.notes)) return highlight;
  const notes = await Promise.all(highlight.notes.map(note => openNote(auth, note)));
  /*
   * ⚠️ IN PLACE, NEVER `{ ...highlight, notes }`. The read path marks a row it
   * opened with a NON-ENUMERABLE property (`cameFromEnvelope`), and a spread
   * drops it. Every highlight the API lists carries a `notes` array, so the
   * spread that used to be here stripped the mark from EVERY highlight: the
   * plaintext migration saw each sealed highlight as work to do again, and the
   * re-seal walk refused every one as "not an envelope" — so a member with a
   * single highlight could never finish "Sign out all other devices"
   * (2026-09-11, 1.3.7-dev.583: "highlight 9e80d590… not-an-envelope").
   * The row is the read path's own object, or the API's for a plaintext row;
   * neither is shared with anything that has seen it yet.
   */
  highlight.notes = notes;
  return highlight;
}

const openBlank = (auth, { programSlug, lessonSlug }, row) =>
  openRow(
    auth,
    ANNOTATION_TYPE.BLANK,
    blankAnswerItemId({
      programSlug: row.programSlug ?? programSlug,
      lessonSlug: row.lessonSlug ?? lessonSlug,
      blockId: row.blockId,
    }),
    row,
  );

// Only a list is opened. Anything else is handed back as it came, so a caller
// that used to throw on an unexpected shape still throws in the same place.
const openList = (rows, open) => (Array.isArray(rows) ? Promise.all(rows.map(open)) : rows);

// The blank-answers endpoint has answered both as a bare list and as `{ data }`;
// a row without a blockId names no blank and has never been shown.
function blankAnswerRows(payload) {
  const rows = Array.isArray(payload) ? payload : payload?.data;
  if (!Array.isArray(rows)) return [];
  return rows.filter(row => row?.blockId !== null && row?.blockId !== undefined);
}

// ── Reads ──────────────────────────────────────────────────────────────────

/** GET /lessons/:slug/highlights?view= — every highlight on one lesson, notes embedded. */
export async function fetchLessonHighlights(auth, { slug, view }) {
  const { data } = await axios.get(
    apiUrl(`/lessons/${slug}/highlights`),
    withAuth(auth, { params: { view } }),
  );
  return openList(data, row => openHighlight(auth, row));
}

/** GET /highlights?view= — every highlight of the member, for the Notes Hub. */
export async function fetchAllHighlights(auth, { view }) {
  const { data } = await axios.get(apiUrl('/highlights'), withAuth(auth, { params: { view } }));
  return openList(data, row => openHighlight(auth, row));
}

/**
 * GET /notes?… — the paged cross-lesson note list. The page object comes back in
 * the shape the API sent it (`{ data, total }` today; `{ items, nextCursor, total }`
 * for an enrolled member, per the API contract) with its rows opened.
 */
export async function fetchAllNotes(auth, params) {
  const { data } = await axios.get(apiUrl('/notes'), withAuth(auth, { params }));
  if (Array.isArray(data?.data)) {
    return { ...data, data: await openList(data.data, row => openNote(auth, row)) };
  }
  if (Array.isArray(data?.items)) {
    return { ...data, items: await openList(data.items, row => openNote(auth, row)) };
  }
  return data;
}

/**
 * GET /notes?cursor=… — one page of the enrolled cursor walk, **unopened**.
 *
 * The corpus stores what the server sent, envelopes and all (#1548, spec 6.7):
 * a device that has to keep a member's whole note set on disk must keep it
 * sealed, and decrypt only into live Vuex state for rendering. So this is the
 * one read that deliberately skips the response hook; `openNoteRow` below is
 * how a caller opens a row on its way out of IndexedDB.
 *
 * Shape is the contract's: `{ items, nextCursor, total }`. `cursor` goes back
 * verbatim as `?cursor=`.
 */
export async function fetchNotesPageRaw(auth, { cursor, view, program } = {}) {
  const params = { view };
  if (program) params.program = program;
  if (cursor) params.cursor = cursor;
  const { data } = await axios.get(apiUrl('/notes'), withAuth(auth, { params }));
  return data;
}

/**
 * GET /notes/:id — one row, unopened, for a corpus refetch. `null` on a 404, so
 * a manifest that names a row deleted between the two calls reconciles rather
 * than throwing.
 */
export async function fetchNoteByIdRaw(auth, id) {
  try {
    countAnnotationRequest(ANNOTATION_REQUEST.NOTE_READ_ONE, id);
    const { data } = await axios.get(apiUrl(`/notes/${id}`), withAuth(auth));
    return data ?? null;
  } catch (error) {
    if (error?.response?.status === 404) return null;
    throw error;
  }
}

/**
 * GET /notes/manifest — `[{ id, updatedAt }]` for every note the member owns,
 * no envelopes. Deliberately NOT scoped by `view`: it lists every note whatever
 * its audience, so a leader-view corpus reconciles against ids the student-view
 * list never returns. The caller must therefore treat an id it does not hold as
 * "not mine to fetch in this scope" rather than as a gap.
 */
export async function fetchNotesManifest(auth, { program } = {}) {
  const params = {};
  if (program) params.program = program;
  const { data } = await axios.get(apiUrl('/notes/manifest'), withAuth(auth, { params }));
  return Array.isArray(data) ? data : [];
}

/**
 * Open one stored note row — the response hook, applied on the way OUT of the
 * corpus rather than on the way in. Same context the live read path builds, so
 * the AAD matches and a row moved between members or ids fails to open exactly
 * as it would over the wire.
 */
export function openNoteRow(auth, row) {
  return openNote(auth, row);
}

/**
 * The same for a blank answer, which is keyed by scope rather than by a row id.
 * The outbox overlay opens a retained entry with it (write-volume spec §7).
 *
 * It was named in the spec and called by `drain.js` from commit 8, but not
 * exported until the composition spec ran the real transport: the drain's own
 * spec mocked the whole module, so the missing export read as a working call,
 * and at runtime the overlay's `try` swallowed the TypeError — every blank
 * overlay silently did nothing.
 */
export function openBlankRow(auth, scope, row) {
  return openBlank(auth, scope, row);
}

/** GET /tags?… — every tag the member owns, across every part and guide. */
export async function fetchTags(auth, params) {
  const { data } = await axios.get(apiUrl('/tags'), withAuth(auth, { params }));
  return openList(data, row => openTag(auth, row));
}

/** GET /lessons/:slug/blank-answers — the member's answers on one lesson, as a list. */
export async function fetchBlankAnswers(auth, { slug, programSlug }) {
  const { data } = await axios.get(apiUrl(`/lessons/${slug}/blank-answers`), withAuth(auth));
  const scope = { programSlug, lessonSlug: slug };
  return Promise.all(blankAnswerRows(data).map(row => openBlank(auth, scope, row)));
}

// ── Highlights ─────────────────────────────────────────────────────────────

/** POST /highlights */
export async function createHighlight(auth, body) {
  const sealed = await sealBody(auth, ANNOTATION_TYPE.HIGHLIGHT, body?.id, body);
  countAnnotationRequest(ANNOTATION_REQUEST.HIGHLIGHT_CREATE, sealed?.id ?? body?.id);
  const { data } = await axios.post(apiUrl('/highlights'), sealed, withWriteAuth(auth));
  return openHighlight(auth, data);
}

/** PATCH /highlights/:id */
export async function updateHighlight(auth, id, patch) {
  const sealed = await sealBody(auth, ANNOTATION_TYPE.HIGHLIGHT, id, patch);
  countAnnotationRequest(ANNOTATION_REQUEST.HIGHLIGHT_UPDATE, id);
  const { data } = await axios.patch(apiUrl(`/highlights/${id}`), sealed, withWriteAuth(auth));
  return openHighlight(auth, data);
}

/** DELETE /highlights/:id */
export async function deleteHighlight(auth, id) {
  countAnnotationRequest(ANNOTATION_REQUEST.HIGHLIGHT_DELETE, id);
  await axios.delete(apiUrl(`/highlights/${id}`), withWriteAuth(auth));
}

// ── Notes ──────────────────────────────────────────────────────────────────

/**
 * POST /notes
 *
 * `onRawRow` is handed the row EXACTLY as the API sent it, before the response
 * hook opens it — see the note on `updateNote` for why anything maintaining the
 * corpus needs that and cannot use the return value.
 */
export async function createNote(auth, body, { onRawRow } = {}) {
  const sealed = await sealBody(auth, ANNOTATION_TYPE.NOTE, body?.id, body);
  countAnnotationRequest(ANNOTATION_REQUEST.NOTE_CREATE, sealed?.id ?? body?.id);
  const { data } = await axios.post(apiUrl('/notes'), sealed, withWriteAuth(auth));
  onRawRow?.(data);
  return openNote(auth, data);
}

/**
 * PATCH /notes/:id
 *
 * TWO ROWS COME BACK FROM ONE RESPONSE, and a caller may need either.
 *
 * What this RETURNS is the row after the response hook: for an enrolled member
 * that means the envelope has been opened, its `payload` dropped and the
 * plaintext fields merged in. That is what the store's live state wants.
 *
 * What `onRawRow` receives is the row as it arrived, envelope intact. The notes
 * corpus stores rows sealed and opens them only on the way out (#1548, spec
 * 6.7), so the corpus sync cannot use the return value — putting it in
 * IndexedDB would write a member's words there in the clear, which is the one
 * thing the corpus design exists to avoid.
 *
 * Before this hook existed the corpus sync re-fetched the row with
 * `fetchNoteByIdRaw` for exactly that reason: it is the same row (the API
 * answers a PATCH from `findOne`, which is also what `GET /notes/:id` returns),
 * so the fetch was a second request for something the response already had —
 * one extra request and three extra queries on EVERY keystroke of a Notes Hub
 * edit. Item 1a of the write-volume plan.
 *
 * Called only when the API answered; a failed request calls neither.
 */
export async function updateNote(auth, id, patch, { onRawRow } = {}) {
  const sealed = await sealBody(auth, ANNOTATION_TYPE.NOTE, id, patch);
  countAnnotationRequest(ANNOTATION_REQUEST.NOTE_UPDATE, id);
  const { data } = await axios.patch(apiUrl(`/notes/${id}`), sealed, withWriteAuth(auth));
  onRawRow?.(data);
  return openNote(auth, data);
}

/** DELETE /notes/:id */
export async function deleteNote(auth, id) {
  countAnnotationRequest(ANNOTATION_REQUEST.NOTE_DELETE, id);
  await axios.delete(apiUrl(`/notes/${id}`), withWriteAuth(auth));
}

/** POST /notes/:id/tags — a link, not content: no hook runs. */
export async function attachNoteTag(auth, noteId, tagId) {
  countAnnotationRequest(ANNOTATION_REQUEST.NOTE_TAG_ATTACH, noteId);
  await axios.post(apiUrl(`/notes/${noteId}/tags`), { tagId }, withWriteAuth(auth));
}

/** DELETE /notes/:id/tags/:tagId — a link, not content: no hook runs. */
export async function detachNoteTag(auth, noteId, tagId) {
  countAnnotationRequest(ANNOTATION_REQUEST.NOTE_TAG_DETACH, noteId);
  await axios.delete(apiUrl(`/notes/${noteId}/tags/${tagId}`), withWriteAuth(auth));
}

// ── Tags ───────────────────────────────────────────────────────────────────

/** POST /tags */
export async function createTag(auth, body) {
  const sealed = await sealBody(auth, ANNOTATION_TYPE.TAG, body?.id, body);
  countAnnotationRequest(ANNOTATION_REQUEST.TAG_CREATE, sealed?.id ?? body?.id);
  const { data } = await axios.post(apiUrl('/tags'), sealed, withWriteAuth(auth));
  return openTag(auth, data);
}

/** PATCH /tags/:id */
export async function updateTag(auth, id, patch) {
  const sealed = await sealBody(auth, ANNOTATION_TYPE.TAG, id, patch);
  countAnnotationRequest(ANNOTATION_REQUEST.TAG_UPDATE, id);
  const { data } = await axios.patch(apiUrl(`/tags/${id}`), sealed, withWriteAuth(auth));
  return openTag(auth, data);
}

/** DELETE /tags/:id — a soft delete server-side, so `restoreTag` can undo it. */
export async function deleteTag(auth, id) {
  countAnnotationRequest(ANNOTATION_REQUEST.TAG_DELETE, id);
  await axios.delete(apiUrl(`/tags/${id}`), withWriteAuth(auth));
}

/**
 * GET /tags/usage?ids=… — how many notes carry each tag, split by part and
 * guide: `{ [tagId]: [{ programSlug, audience, count }] }`.
 *
 * Counts, not content: no hook runs on either side. Ids the member does not own
 * are simply absent from the answer, so a stale id costs nothing.
 */
export async function fetchTagUsage(auth, ids) {
  const list = (ids || []).filter(Boolean);
  if (!list.length) return {};
  const { data } = await axios.get(
    apiUrl('/tags/usage'),
    withAuth(auth, { params: { ids: list.join(',') } }),
  );
  return data || {};
}

/**
 * POST /tags/:id/restore — undo of a delete, within the server's window.
 *
 * Answers `{ tag, noteIds }`: the tag row (opened like any other read) and the
 * notes whose links came back with it, kept apart so no caller has to unpick a
 * "tag" that is carrying a field no tag has. The device cannot reconstruct that
 * list — the links it never saw belong to notes in another part or guide — which
 * is the whole reason the server keeps them rather than the toast.
 */
export async function restoreTag(auth, id) {
  const { data } = await axios.post(apiUrl(`/tags/${id}/restore`), {}, withWriteAuth(auth));
  if (!data) return { tag: null, noteIds: [] };
  // Split BEFORE opening, not after: the response hook is handed a row and
  // returns a row, so `noteIds` left on the way in comes back out on the tag —
  // which is the thing this shape exists to prevent.
  const { noteIds = [], ...row } = data;
  return { tag: await openTag(auth, row), noteIds };
}

// ── Blank answers ──────────────────────────────────────────────────────────

/**
 * DELETE /blank-answers?programSlug=&lessonSlug=&blockId= — the row keyed by the
 * upsert's own three keys, in the query string rather than the path because
 * that is how the API takes it (contract section 3). 204 whether or not a row
 * existed, so a caller never has to check first.
 *
 * `expectedRevision` makes it conditional, and it is OPTIONAL in the same way
 * every other annotation write's is: a device that has not read the row names
 * no revision and clears it unconditionally, exactly as before. Sent as a query
 * parameter because a DELETE carries no body. An API that does not honour it
 * yet ignores it, which is the unconditional clear this always was.
 */
export async function deleteBlankAnswer(
  auth,
  { programSlug, lessonSlug, blockId, expectedRevision },
) {
  countAnnotationRequest(
    ANNOTATION_REQUEST.BLANK_DELETE,
    blankAnswerItemId({ programSlug, lessonSlug, blockId }),
  );
  await axios.delete(
    apiUrl('/blank-answers'),
    withWriteAuth(auth, {
      params: {
        programSlug,
        lessonSlug,
        blockId,
        ...(expectedRevision === undefined || expectedRevision === null
          ? {}
          : { expectedRevision }),
      },
    }),
  );
}

/**
 * Seal a body without sending it, so it can be written to the durable outbox
 * before the request is attempted (write-volume plan, phase 3).
 *
 * The outbox holds envelopes, never plaintext — an entry is often the only copy
 * of what a member typed, and it has to be safe at rest to survive a sign-out at
 * all. Sealing therefore has to happen BEFORE the send rather than inside it,
 * which is the one thing the per-type write functions cannot do for a caller.
 *
 * Returns whatever the request hook returns: the sealed body, the body unchanged
 * for a member who is not enrolled, or the `DELETE_BLANK_ANSWER` sentinel for an
 * emptied blank. Throws a `SealedWriteError` exactly as a write would, so a
 * refusal is caught before anything is persisted.
 */
export function sealAnnotation(auth, type, itemId, body, options) {
  return sealBody(auth, type, itemId, body, options);
}

/**
 * Put an ALREADY-SEALED body on the wire. Same requests, and the same opening on
 * the way back, as the per-type writes — it just skips the sealing, because the
 * caller did that when it persisted the entry.
 *
 * An emptied blank on a blank means the seal step turned the write into a
 * delete. `isDeleteBlankAnswer` recognises both shapes it can arrive in — the
 * frozen sentinel, and the object Preferences parsed back out of the outbox —
 * and the revision it carries goes on the wire with it.
 *
 * @param {object} auth
 * @param {{ type: string, itemId: string, sealed: object|null, scope?: object,
 *   onRawRow?: (row: object) => void }} args
 */
export async function sendSealed(auth, { type, itemId, sealed, scope, onRawRow }) {
  if (type === ANNOTATION_TYPE.BLANK) {
    if (sealed === null || isDeleteBlankAnswer(sealed)) {
      // The revision travels ON the sentinel, so a delete replayed from the
      // outbox is as conditional as the one that was queued.
      await deleteBlankAnswer(auth, { ...scope, expectedRevision: sealed?.expectedRevision });
      return null;
    }
    countAnnotationRequest(ANNOTATION_REQUEST.BLANK_UPSERT, itemId);
    const { data } = await axios.put(apiUrl('/blank-answers'), sealed, withWriteAuth(auth));
    onRawRow?.(data);
    return data ? openBlank(auth, scope, data) : data;
  }
  if (type === ANNOTATION_TYPE.NOTE) {
    countAnnotationRequest(ANNOTATION_REQUEST.NOTE_UPDATE, itemId);
    const { data } = await axios.patch(apiUrl(`/notes/${itemId}`), sealed, withWriteAuth(auth));
    onRawRow?.(data);
    return openNote(auth, data);
  }
  throw new TypeError(`sendSealed: no route for ${type}`);
}

/**
 * PUT /blank-answers — keyed on programSlug, lessonSlug and blockId.
 *
 * The one write whose HTTP verb the sealing hook can change: an emptied blank
 * deletes its row for an enrolled member, so a hook answering
 * DELETE_BLANK_ANSWER sends the delete above instead and this returns null,
 * which is what a PUT with no body already returned.
 */
export async function upsertBlankAnswer(auth, body) {
  const sealed = await sealBody(auth, ANNOTATION_TYPE.BLANK, blankAnswerItemId(body), body);
  if (isDeleteBlankAnswer(sealed)) {
    await deleteBlankAnswer(auth, { ...body, expectedRevision: sealed.expectedRevision });
    return null;
  }
  countAnnotationRequest(ANNOTATION_REQUEST.BLANK_UPSERT, blankAnswerItemId(body));
  const { data } = await axios.put(apiUrl('/blank-answers'), sealed, withWriteAuth(auth));
  return data ? openBlank(auth, body, data) : data;
}
